import { useTheme } from 'next-themes';
import type React from 'react';
import { createContext, useContext } from 'react';
import { Toaster, toast } from 'sonner';

/**
 * Transient notifications (sonner, as NextAdmin mounts it) — the replacement for `alert()`.
 *
 * Native `alert()`/`confirm()` block the event loop, cannot be styled and stop browser automation dead, so they are
 * banned. Use this for "it worked" / "it did not"; ask before destructive actions with `ConfirmDialog`.
 *
 *   const { notify } = useToast();
 *   notify('Workflow saved');                          // success (default)
 *   notify('Could not save the workflow', 'danger');
 */

export type ToastTone = 'success' | 'danger' | 'info';

interface ToastContextValue {
  notify: (message: string, tone?: ToastTone) => void;
}

const DEFAULT_TTL_MS = 5_000;

const SHOW: Record<ToastTone, (message: string) => void> = {
  success: (message) => toast.success(message),
  danger: (message) => toast.error(message),
  info: (message) => toast.info(message),
};

const VALUE: ToastContextValue = { notify: (message, tone = 'success') => SHOW[tone](message) };

const ToastContext = createContext<ToastContextValue | null>(null);

/** Mount once at the app root: provides `useToast` and renders the toast stack in the active theme. */
export function ToastProvider({ children, ttlMs = DEFAULT_TTL_MS }: { children: React.ReactNode; ttlMs?: number }) {
  const { resolvedTheme } = useTheme();
  return (
    <ToastContext.Provider value={VALUE}>
      {children}
      <Toaster
        theme={resolvedTheme === 'dark' ? 'dark' : 'light'}
        position="bottom-right"
        duration={ttlMs}
        richColors
        closeButton
      />
    </ToastContext.Provider>
  );
}

/**
 * Throws when used outside the provider. Deliberately loud: a silently no-op `notify` would reintroduce exactly the
 * failure this replaces — an action reporting nothing to the user.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>. Mount it once at the app root.');
  return ctx;
}
